/**
 * EveningReviewWizard
 *
 * 3-step evening review wizard for reflecting on the day.
 * Supports per-step toggles (same pattern as DailyPlanningWizard).
 */

import { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Check, Circle, Clock, Calendar, Info } from 'lucide-react';
import type { EventInstance, TimeFormat, CalendarSettings } from '../types';
import type { SchedulableTaskDetail } from '@/lib/api';

interface EveningReviewWizardProps {
  open: boolean;
  onClose: () => void;
  todayEvents: EventInstance[];
  tasks: SchedulableTaskDetail[];
  todayPriorities: string[];
  onCompleteTask: (taskId: string) => Promise<void>;
  onScheduleTask: (taskId: string, hour: number, durationMinutes: number) => void;
  onSaveReview: (reflection: string, completedCount: number, plannedCount: number) => void;
  timeFormat?: TimeFormat;
  stepToggles: Pick<
    CalendarSettings,
    'showEveningDaySummary' | 'showEveningReschedule' | 'showEveningReflection'
  >;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
}

function formatTimeLocal(dateStr: string, timeFormat: TimeFormat = '12h'): string {
  return new Date(dateStr).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
}

/** Setting key for each wizard step index. */
const STEP_SETTING_KEYS: (keyof Pick<
  CalendarSettings,
  'showEveningDaySummary' | 'showEveningReschedule' | 'showEveningReflection'
>)[] = ['showEveningDaySummary', 'showEveningReschedule', 'showEveningReflection'];

const STEP_TOGGLE_LABELS = ['Show Day Summary', 'Show Reschedule', 'Show Reflection'];

export function EveningReviewWizard({
  open,
  onClose,
  todayEvents,
  tasks,
  todayPriorities,
  onCompleteTask,
  onScheduleTask,
  onSaveReview,
  timeFormat = '12h',
  stepToggles,
  onUpdateSettings,
}: EveningReviewWizardProps) {
  const [step, setStep] = useState(0);
  const [reflection, setReflection] = useState('');

  const completedTasks = tasks.filter((t) => t.status === 'completed');
  const incompleteTasks = tasks.filter((t) => t.status === 'pending');
  const totalPlanned = tasks.filter((t) => t.status !== 'cancelled').length;

  /** Ordered indices of steps that are enabled. */
  const enabledSteps = useMemo(() => {
    const enabled: number[] = [];
    if (stepToggles.showEveningDaySummary) enabled.push(0);
    if (stepToggles.showEveningReschedule) enabled.push(1);
    if (stepToggles.showEveningReflection) enabled.push(2);
    return enabled;
  }, [stepToggles]);

  /** The actual step index (0-2) for the current position in the enabled list. */
  const currentStepIndex = enabledSteps[step] ?? 0;
  const isLastStep = step >= enabledSteps.length - 1;
  const totalEnabledSteps = enabledSteps.length;

  const goNext = useCallback(() => {
    if (!isLastStep) setStep((s) => s + 1);
  }, [isLastStep]);

  const goBack = useCallback(() => {
    if (step > 0) setStep((s) => s - 1);
  }, [step]);

  const handleClose = () => {
    onClose();
    setStep(0);
    setReflection('');
  };

  const handleSaveAndClose = () => {
    onSaveReview(reflection, completedTasks.length, totalPlanned);
    handleClose();
  };

  // For step 1: rescheduling to tomorrow
  const handleRescheduleTomorrow = (taskId: string, task: SchedulableTaskDetail) => {
    // Schedule to 9am tomorrow, using task's estimated duration
    onScheduleTask(taskId, 9, task.estimatedMinutes || 60);
  };

  const stepTitles = ['How did today go?', 'Reschedule incomplete items', 'Reflection'];
  const stepDescriptions = [
    'Review your day — see what you completed, what events you had, and how your focus priorities went.',
    'Decide what to do with tasks that are still incomplete — reschedule them for tomorrow or mark them done.',
    'Take a moment to reflect on how today went. Your notes are saved for future reference.',
  ];

  /** Toggle row shown at the bottom of each step to disable it. */
  const stepToggle = (
    <div className="flex items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2 shrink-0">
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-[12px] text-muted-foreground leading-none">
          {STEP_TOGGLE_LABELS[currentStepIndex]}
        </span>
        <span className="relative group">
          <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-48 rounded-md bg-popover border px-2.5 py-1.5 text-[11px] font-normal text-popover-foreground shadow-md opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
            This step can be re-enabled in Settings &rarr; Evening Review.
          </span>
        </span>
      </div>
      <Switch
        checked={stepToggles[STEP_SETTING_KEYS[currentStepIndex]]}
        onCheckedChange={(checked) =>
          onUpdateSettings({ [STEP_SETTING_KEYS[currentStepIndex]]: checked })
        }
        className="shrink-0"
      />
    </div>
  );

  // If all steps are disabled, just close
  if (open && enabledSteps.length === 0) {
    handleClose();
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-lg h-[28rem] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {stepTitles[currentStepIndex]}
            <span className="relative group">
              <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-64 rounded-md bg-popover border px-3 py-2 text-xs font-normal text-popover-foreground shadow-md opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-opacity z-50">
                {stepDescriptions[currentStepIndex]}
              </span>
            </span>
          </DialogTitle>
          <DialogDescription>
            Step {step + 1} of {totalEnabledSteps}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 0: Review today */}
          {currentStepIndex === 0 && (
            <div className="space-y-4">
              <div className="text-sm font-medium text-center py-2 bg-muted rounded-md">
                You completed {completedTasks.length} of {totalPlanned} planned items
              </div>

              {todayEvents.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Today's events</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {todayEvents
                      .sort((a, b) => {
                        if (a.isAllDay && !b.isAllDay) return -1;
                        if (!a.isAllDay && b.isAllDay) return 1;
                        return new Date(a.startAt).getTime() - new Date(b.startAt).getTime();
                      })
                      .map((evt) => (
                        <div key={evt.id} className="flex items-center gap-2 text-sm py-1">
                          {evt.isAllDay ? (
                            <Calendar className="h-3 w-3 text-muted-foreground shrink-0" />
                          ) : (
                            <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
                          )}
                          <span className="text-muted-foreground w-24 shrink-0">
                            {evt.isAllDay
                              ? 'All day'
                              : `${formatTimeLocal(evt.startAt, timeFormat)} – ${formatTimeLocal(evt.endAt, timeFormat)}`}
                          </span>
                          <span className="truncate">{evt.title}</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium mb-2">Tasks</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {completedTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 text-sm py-1 text-green-600"
                    >
                      <Check className="h-3 w-3 shrink-0" />
                      <span className="line-through">{task.title}</span>
                    </div>
                  ))}
                  {incompleteTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-center gap-2 text-sm py-1 text-amber-600"
                    >
                      <Circle className="h-3 w-3 shrink-0" />
                      <span>{task.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              {todayPriorities.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Today's focus priorities</h4>
                  <div className="flex flex-wrap gap-2">
                    {todayPriorities.map((p) => (
                      <Badge key={p} variant="secondary">
                        {p}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Reschedule incomplete items */}
          {currentStepIndex === 1 && (
            <div className="space-y-3">
              {incompleteTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No incomplete tasks to reschedule.</p>
              ) : (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {incompleteTasks.map((task) => (
                    <div key={task.id} className="border rounded-md p-3 space-y-2">
                      <div className="text-sm font-medium">{task.title}</div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => handleRescheduleTomorrow(task.id, task)}
                        >
                          Reschedule to tomorrow
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={() => onCompleteTask(task.id)}
                        >
                          Mark complete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Reflection */}
          {currentStepIndex === 2 && (
            <div className="space-y-4">
              <Textarea
                value={reflection}
                onChange={(e) => setReflection(e.target.value.slice(0, 500))}
                placeholder="How did today go? Any takeaways?"
                rows={5}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground text-right">{reflection.length}/500</p>
            </div>
          )}
        </div>

        {stepToggle}

        <DialogFooter className="flex justify-between gap-2 sm:justify-between shrink-0">
          <div>
            {step > 0 && (
              <Button variant="ghost" size="sm" onClick={goBack}>
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {!isLastStep && (
              <>
                <Button variant="ghost" size="sm" onClick={goNext}>
                  Skip
                </Button>
                <Button size="sm" onClick={goNext}>
                  Next
                </Button>
              </>
            )}
            {isLastStep && (
              <Button size="sm" onClick={handleSaveAndClose}>
                Save & Close
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
